import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as DropdownIcon } from "../../asserts/logo/dropdown.svg";
import warningTriangleIcon from "../../asserts/logo/WarningTraingleIcon.svg";

const DIRECTION_OPTIONS = [
  { value: "gte", label: "≥ (greater or equal)", symbol: "≥" },
  { value: "lte", label: "≤ (less or equal)", symbol: "≤" },
  { value: "lt", label: "< (less than)", symbol: "<" },
  { value: "eq", label: "= (equal)", symbol: "=" },
];

const ON_FAIL_OPTIONS = [
  { value: "block_release", label: "Block release - require manual review" },
  { value: "warn_continue", label: "Warn and continue" },
  { value: "flag_only", label: "Flag for later review" },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    bgcolor: "#fff",
    fontSize: "16px",
    "& fieldset": { borderColor: "#707070" },
    "&:hover fieldset": { borderColor: "#98A2B3" },
    "&.Mui-focused fieldset": { borderColor: "#555555", borderWidth: 1 },
  },
};

const selectSx = {
  borderRadius: "4px",
  fontSize: "16px",
  color: "#707070",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#707070" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#555555" },
};

function RequiredMark() {
  return (
    <Typography component="span" sx={{ color: "#DE350B", ml: 0.25, fontWeight: 700 }}>
      *
    </Typography>
  );
}

function FieldLabel({ children, required }) {
  return (
    <Typography
      sx={{
        fontFamily: "Lato, sans-serif",
        fontWeight: 700,
        fontStyle: "normal",
        fontSize: "16px",
        lineHeight: "24px",
        letterSpacing: 0,
        color: "#555555",
        mb: 0.75,
      }}
    >
      {required ? <RequiredMark /> : null}
      {children}
    </Typography>
  );
}

function SubFieldLabel({ children }) {
  return (
    <Typography
      sx={{
        fontFamily: "Lato, sans-serif",
        fontWeight: 700,
        fontStyle: "normal",
        fontSize: "16px",
        lineHeight: "24px",
        letterSpacing: 0,
        color: "#707070",
        mb: 0.75,
      }}
    >
      {children}
    </Typography>
  );
}

function directionSymbol(direction) {
  return DIRECTION_OPTIONS.find((o) => o.value === direction)?.symbol || "≥";
}

function formatBaselineBanner(baseline) {
  if (!baseline || baseline.threshold == null || !Number.isFinite(Number(baseline.threshold))) {
    return null;
  }
  const sym = directionSymbol(baseline.direction || "gte");
  const value = Number(baseline.threshold);
  return `Industry baseline for this metric is ${sym} ${value}. Setting a stricter bar is recommended for customer-facing AI.`;
}

/**
 * Configure threshold / direction / on-fail for a metric (Canonical or Derived).
 */
export default function ConfigureThresholdDialog({ metric, onClose, onSave, submitting }) {
  const open = Boolean(metric);
  const [threshold, setThreshold] = useState("");
  const [direction, setDirection] = useState("gte");
  const [onFailAction, setOnFailAction] = useState("block_release");

  useEffect(() => {
    if (!metric) return;
    setThreshold(
      metric.threshold == null || Number.isNaN(Number(metric.threshold))
        ? ""
        : String(metric.threshold)
    );
    setDirection(metric.direction || "gte");
    setOnFailAction(metric.onFailAction || "block_release");
  }, [metric]);

  const baselineText = useMemo(
    () => formatBaselineBanner(metric?.catalogBaseline),
    [metric]
  );

  const isCanonical = metric?.source === "Generated";
  const titleName = metric?.metricName || "Metric";

  const handleSave = async () => {
    const n = Number(threshold);
    if (!Number.isFinite(n)) return;
    await onSave({
      threshold: n,
      direction,
      on_fail_action: onFailAction,
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 480, md: 520 },
          maxWidth: "100vw",
          boxShadow: "-8px 0 32px rgba(16, 24, 40, 0.14)",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(52, 64, 84, 0.45)" },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", bgcolor: "#fff" }}>
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid #EAECF0" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
            <Typography
              sx={{
                fontFamily: "Lato, sans-serif",
                fontWeight: 700,
                fontStyle: "normal",
                fontSize: "16px",
                lineHeight: "24px",
                letterSpacing: 0,
                color: "#555555",
                pr: 1,
              }}
            >
              Configure threshold - {titleName}
            </Typography>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Close"
              sx={{ color: "#555555", mt: -0.5, mr: -0.5, flexShrink: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontFamily: "Lato, sans-serif",
              fontWeight: 400,
              fontStyle: "normal",
              fontSize: "14px",
              lineHeight: "24px",
              letterSpacing: 0,
              color: "#555555",
              mt: 1,
              pr: 2,
            }}
          >
            Set the acceptance bar this metric must clear before Gate-1 can be signed off.
          </Typography>
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2.5 }}>
          <Stack spacing={2.5}>
            {baselineText ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  bgcolor: "#FFFAEB",
                  border: "1px solid #FFF8E0",
                  borderRadius: "8px",
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Box
                  component="img"
                  src={warningTriangleIcon}
                  alt=""
                  sx={{
                    width: 20,
                    height: 18,
                    mt: "3px",
                    ml: "2px",
                    flexShrink: 0,
                    display: "block",
                    opacity: 1,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: "Lato, sans-serif",
                    fontWeight: 400,
                    fontStyle: "normal",
                    fontSize: "14px",
                    lineHeight: "21px",
                    letterSpacing: 0,
                    verticalAlign: "middle",
                    color: "#8A5C00",
                    flex: 1,
                  }}
                >
                  {baselineText}
                </Typography>
              </Box>
            ) : null}

            <Box>
              <FieldLabel required>Threshold</FieldLabel>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                  alignItems: "start",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <SubFieldLabel>Operator</SubFieldLabel>
                  <FormControl fullWidth size="small">
                    <Select IconComponent={DropdownIcon}
                      value={direction || "gte"}
                      onChange={(e) => setDirection(e.target.value)}
                      sx={selectSx}
                    >
                      {DIRECTION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <SubFieldLabel>Value</SubFieldLabel>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ step: "0.01" }}
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    sx={fieldSx}
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              <SubFieldLabel>If the metric fails on a run</SubFieldLabel>
              <FormControl fullWidth size="small">
                <Select IconComponent={DropdownIcon}
                  value={onFailAction}
                  onChange={(e) => setOnFailAction(e.target.value)}
                  sx={selectSx}
                >
                  {ON_FAIL_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid #EAECF0",
            display: "flex",
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            flexDirection: { xs: "column", sm: "row" },
            bgcolor: "#fff",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Lato, sans-serif",
              fontWeight: 400,
              fontStyle: "normal",
              fontSize: "13px",
              lineHeight: "24px",
              letterSpacing: 0,
              color: "#555555",
              flex: 1,
            }}
          >
            {isCanonical
              ? "Canonical metric · changes require approver sign-off"
              : "Derived metric · you can edit or delete it any time"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", flexShrink: 0 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#005BF0",
                borderColor: "#1A6EF5",
                borderRadius: "4px",
                px: 2,
                "&:hover": { borderColor: "#1251c0", bgcolor: "rgba(26, 110, 245, 0.04)" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={submitting || !Number.isFinite(Number(threshold))}
              onClick={handleSave}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#005BF0",
                borderRadius: "4px",
                px: 2,
                boxShadow: "none",
                "&:hover": { bgcolor: "#1251c0", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "#98A2B3", color: "#fff" },
              }}
            >
              {submitting ? "Saving…" : "Apply Threshold"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
